import { inject, Injectable } from '@angular/core';
import { CapacitorService, ErrorHandlerService, MaintenanceService } from '@libs/shared/common';
import { CartModel, ShopErrorItemModel } from '@libs/shop/models';
import { StatusCodes } from 'http-status-codes';
import { catchError, firstValueFrom, interval, of, Subscription, tap } from 'rxjs';
import { ShopErrorParserService } from '../shop-error-parser';
import { CartService } from './cart.service';

@Injectable({ providedIn: 'root' })
export class CartPollingService {
  private readonly capacitorService: CapacitorService = inject(CapacitorService);
  private readonly cartService: CartService = inject(CartService);
  private readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);
  private readonly shopErrorParser: ShopErrorParserService = inject(ShopErrorParserService);
  private readonly maintenanceService: MaintenanceService = inject(MaintenanceService);

  private pollingSubscription$: Subscription | undefined;
  private eventSubscriptions: Subscription = new Subscription();
  private readonly pollingInterval: number = 30000;

  public init(): void {
    this.eventSubscriptions = new Subscription();
    this.initPolling();

    this.eventSubscriptions.add(this.capacitorService.pause$().subscribe(() => {
      this.stopPolling();
    }));

    this.eventSubscriptions.add(this.capacitorService.resume$().subscribe(() => {
      void this.refreshCart();
      this.initPolling();
    }));
  }

  public dispose(): void {
    this.stopPolling();
    this.eventSubscriptions.unsubscribe();
  }

  public refreshCart(): Promise<CartModel | undefined> {
    if (this.maintenanceService.maintenanceModeEnabled()) {
      return Promise.resolve(undefined);
    }

    return firstValueFrom(this.cartService.requestCart().pipe(tap((cart: CartModel | undefined) => {
        if (cart) {
          this.cartService.setCart(cart);
        } else {
          this.handleError(new Error('Cart was undefined while polling changes. refreshing cart id'));
          void this.refreshCartId();
        }
      }),
      catchError((error: unknown) => {
        this.handleError(error);

        return of(undefined);
      })));
  }

  private initPolling(): void {
    this.stopPolling();
    this.pollingSubscription$ = interval(this.pollingInterval).subscribe(() => {
      void this.refreshCart();
    });
  }

  private refreshCartId(): Promise<string> {
    return firstValueFrom(this.cartService.initCartId());
  }

  private stopPolling(): void {
    if (this.pollingSubscription$) {
      this.pollingSubscription$.unsubscribe();
      this.pollingSubscription$ = undefined;
    }
  }

  private handleError(error: unknown): void {
    const errors: ShopErrorItemModel[] = this.shopErrorParser.tryParseErrors(error);

    if (errors.some(x => x.status === Number(StatusCodes.NOT_FOUND))) {
      void this.refreshCartId();
    }

    this.errorHandler.handleError(error);
  }
}
